import React, { useState } from 'react';
import { Calendar, Clock, Globe, MapPin, Mail, ArrowRight, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

// Common timezones
const TIMEZONES = [
  { value: 'Europe/London', label: 'London (GMT/BST)', offset: 'GMT+0/+1' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)', offset: 'GMT+1/+2' },
  { value: 'America/New_York', label: 'New York (EST/EDT)', offset: 'GMT-5/-4' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)', offset: 'GMT-6/-5' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)', offset: 'GMT-8/-7' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)', offset: 'GMT+4' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)', offset: 'GMT+8' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', offset: 'GMT+9' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)', offset: 'GMT+10/+11' },
  { value: 'Africa/Lagos', label: 'Lagos (WAT)', offset: 'GMT+1' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)', offset: 'GMT+2' },
];

// Events array - add real events here when scheduled
const EVENTS = [];

// Format time for display in a specific timezone
const formatTimeInTimezone = (date, time, fromTz, toTz) => {
  const dateTime = new Date(`${date}T${time}:00`);
  return dateTime.toLocaleString('en-GB', {
    timeZone: toTz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

// Get current time in a specific timezone
const formatCurrentTime = (timezone) => {
  return new Date().toLocaleString('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const CalendarPage = () => {
  const [selectedTimezone, setSelectedTimezone] = useState('Europe/London');
  const [currentDate, setCurrentDate] = useState(new Date(2026, 2, 1)); // March 2026
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [worldClockTime, setWorldClockTime] = useState(new Date());

  // Update world clock every second
  React.useEffect(() => {
    const interval = setInterval(() => {
      setWorldClockTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  };

  const { daysInMonth, startingDay } = getDaysInMonth(currentDate);

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const getEventsForDay = (day) => {
    const checkDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return EVENTS.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.toDateString() === checkDate.toDateString();
    });
  };

  const hasEvent = (day) => getEventsForDay(day).length > 0;

  const upcomingEvents = EVENTS.filter(e => new Date(e.date) >= new Date())
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <div className="min-h-screen bg-white pt-12 pb-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-navy to-slate-800 text-white py-16 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Calendar size={32} className="text-gold" />
            <span className="text-gold text-sm font-semibold uppercase tracking-wider">Events & Speaking</span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Calendar & Availability
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mb-8">
            View upcoming speaking engagements, workshops, and webinars. All times shown are automatically converted to your selected timezone.
          </p>

          {/* Timezone Selector */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-3">
              <Globe size={18} className="text-gold" />
              <span className="text-sm">Your timezone:</span>
              <select
                data-testid="timezone-selector"
                value={selectedTimezone}
                onChange={(e) => setSelectedTimezone(e.target.value)}
                className="bg-transparent border-0 text-white font-medium cursor-pointer focus:outline-none"
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value} className="text-navy">
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="text-sm text-slate-400">
              Current time: <span className="text-white font-medium">{formatCurrentTime(selectedTimezone)}</span>
            </div>
          </div>
        </div>
      </section>

      {/* World Clock Strip */}
      <section className="bg-soft-grey border-b border-slate-200 py-6 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <Clock size={16} className="text-navy" />
            <span className="text-sm font-semibold text-navy">World Clock</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {['Europe/London', 'America/New_York', 'Asia/Singapore', 'Asia/Dubai', 'Africa/Lagos', 'Australia/Sydney'].map((tz) => {
              const tzInfo = TIMEZONES.find(t => t.value === tz);
              return (
                <div
                  key={tz}
                  data-testid={`world-clock-${tz.replace('/', '-')}`}
                  className={`text-center p-3 rounded-lg transition-colors ${selectedTimezone === tz ? 'bg-navy text-white' : 'bg-white'}`}
                >
                  <p className="text-xs opacity-70 mb-1">{tzInfo?.label.split(' ')[0]}</p>
                  <p className="text-xl font-bold">{formatCurrentTime(tz)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Calendar Widget */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
                {/* Calendar Header */}
                <div className="bg-navy text-white p-4">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={prevMonth}
                      data-testid="calendar-prev-month"
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <h3 className="font-serif text-xl font-bold">
                      {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                    </h3>
                    <button
                      onClick={nextMonth}
                      data-testid="calendar-next-month"
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>

                {/* Calendar Grid */}
                <div className="p-4">
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {dayNames.map((day) => (
                      <div key={day} className="text-center text-xs font-semibold text-slate-500 py-2">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Days */}
                  <div className="grid grid-cols-7 gap-1">
                    {/* Empty cells */}
                    {Array.from({ length: startingDay }).map((_, i) => (
                      <div key={`empty-${i}`} className="aspect-square" />
                    ))}

                    {/* Day cells */}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                      const day = i + 1;
                      const isToday = new Date().toDateString() ===
                        new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();
                      const dayEvents = getEventsForDay(day);

                      return (
                        <div
                          key={day}
                          data-testid={`calendar-day-${day}`}
                          onClick={() => dayEvents.length > 0 && setSelectedEvent(dayEvents[0])}
                          className={`aspect-square flex flex-col items-center justify-center rounded-lg relative cursor-pointer transition-colors ${isToday
                              ? 'bg-navy text-white'
                              : hasEvent(day)
                                ? 'bg-gold/20 hover:bg-gold/30'
                                : 'hover:bg-slate-100'
                            }`}
                          title={dayEvents.map(e => e.title).join(', ')}
                        >
                          <span className={`text-sm ${isToday ? 'font-bold' : ''}`}>{day}</span>
                          {hasEvent(day) && (
                            <div className="absolute bottom-1 w-1.5 h-1.5 bg-gold rounded-full" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Legend */}
                <div className="border-t border-slate-200 p-4 flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-navy rounded"></div>
                    <span className="text-slate-600">Today</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gold/30 rounded"></div>
                    <span className="text-slate-600">Event</span>
                  </div>
                </div>
              </div>

              {/* Selected Event Detail */}
              {selectedEvent && (
                <div className="mt-6 bg-soft-grey rounded-xl p-6 border border-slate-200" data-testid="event-detail">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full mb-2 ${selectedEvent.isVirtual ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                        }`}>
                        {selectedEvent.type}
                      </span>
                      <h4 className="font-serif text-xl font-bold text-navy">{selectedEvent.title}</h4>
                    </div>
                    <button
                      onClick={() => setSelectedEvent(null)}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      ✕
                    </button>
                  </div>

                  <p className="text-slate-600 mb-4">{selectedEvent.description}</p>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar size={16} className="text-gold" />
                      <span>{new Date(selectedEvent.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Clock size={16} className="text-gold" />
                      <span>
                        {formatTimeInTimezone(selectedEvent.date, selectedEvent.time, selectedEvent.timezone, selectedTimezone)} ({TIMEZONES.find(t => t.value === selectedTimezone)?.label.split(' ')[0]})
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin size={16} className="text-gold" />
                      <span>{selectedEvent.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Globe size={16} className="text-gold" />
                      <span>{selectedEvent.isVirtual ? 'Virtual Event' : 'In-Person'}</span>
                    </div>
                  </div>

                  {selectedEvent.spotsAvailable ? (
                    <a
                      href="/contact"
                      className="inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 rounded-lg font-semibold hover:bg-gold/90 transition-colors"
                    >
                      <span>Register Interest</span>
                      <ArrowRight size={18} />
                    </a>
                  ) : (
                    <span className="inline-block px-4 py-2 bg-slate-200 text-slate-500 rounded-lg text-sm">
                      Fully Booked
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar - Upcoming Events */}
            <div>
              <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden sticky top-24">
                <div className="bg-navy text-white p-4">
                  <h3 className="font-serif text-lg font-bold">Upcoming Events</h3>
                </div>

                <div className="divide-y divide-slate-200">
                  {upcomingEvents.length === 0 ? (
                    <div className="p-6 text-center text-slate-500">
                      <Calendar size={32} className="mx-auto mb-2 opacity-50" />
                      <p>No upcoming events scheduled</p>
                    </div>
                  ) : (
                    upcomingEvents.map((event) => (
                      <div
                        key={event.id}
                        data-testid={`upcoming-event-${event.id}`}
                        className="p-4 hover:bg-soft-grey transition-colors cursor-pointer"
                        onClick={() => setSelectedEvent(event)}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${event.isVirtual ? 'bg-blue-100' : 'bg-green-100'
                            }`}>
                            {event.isVirtual ? (
                              <Globe size={18} className="text-blue-600" />
                            ) : (
                              <MapPin size={18} className="text-green-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-navy text-sm truncate">{event.title}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {new Date(event.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} •{' '}
                              {formatTimeInTimezone(event.date, event.time, event.timezone, selectedTimezone)}
                            </p>
                            <p className="text-xs text-slate-400">{event.location}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* CTA */}
                <div className="p-4 bg-soft-grey border-t border-slate-200">
                  <p className="text-sm text-slate-600 mb-3">
                    Want to book Promise Monday for your event?
                  </p>
                  <a
                    href="/speaking"
                    className="flex items-center justify-center gap-2 bg-navy text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-navy/90 transition-colors"
                  >
                    <span>Speaking Enquiries</span>
                    <ExternalLink size={14} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 px-6 lg:px-8 bg-soft-grey">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-navy mb-4">
            Book a Speaking Engagement
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Invite Promise Monday to speak at your conference, workshop, or corporate event. Available for in-person and virtual engagements worldwide.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/speaking"
              className="inline-flex items-center gap-2 bg-navy text-white px-8 py-3 rounded-lg font-semibold hover:bg-navy/90 transition-colors"
            >
              View Speaking Topics
              <ArrowRight size={18} />
            </a>
            <a
              href="mailto:speaking@thirdcultureleadership.co.uk"
              className="inline-flex items-center gap-2 bg-gold text-navy px-8 py-3 rounded-lg font-semibold hover:bg-gold/90 transition-colors"
            >
              <Mail size={18} />
              Contact Directly
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CalendarPage;
